import { Building2, Code2, History, Home, LogOut, Menu, PanelLeftClose, PanelLeftOpen, Settings, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMerchantAuth } from '../contexts/MerchantAuthContext';
import { cabinetPath } from '../lib/domains';
import { isMerchantImpersonationActive } from '../lib/merchantSession';
import { resolveMerchantFiatCurrency } from '../lib/typicalV2';
import './merchant-layout.css';
import { ThemeToggle } from './ThemeToggle';
import { Button } from './ui/button';

const sections = [
    { path: '/dashboard', label: 'Главная', icon: Home },
    { path: '/payments-history', label: 'История платежей', icon: History },
    { path: '/topups-history', label: 'История пополнений', icon: History },
    { path: '/integration', label: 'API и интеграция', icon: Code2 },
    { path: '/settings', label: 'Настройки', icon: Settings },
];
const toolTitles: Record<string, string> = {
    '/sandbox': 'Тестовая среда',
    '/api-test': 'Тестирование платежей',
    '/appeal-test': 'Тестирование апелляций',
};

export function MerchantLayout() {
    const { user, logout } = useMerchantAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const menuButton = useRef<HTMLButtonElement>(null);
    const drawer = useRef<HTMLElement>(null);
    const pathname = location.pathname.replace(/\/+$/, '');
    const toolPath = Object.keys(toolTitles).find(path => pathname === cabinetPath('merchant', path));
    const current = sections.find(section => pathname === cabinetPath('merchant', section.path));
    const title = toolPath ? toolTitles[toolPath] : current?.label || 'Кабинет мерчанта';

    useEffect(() => { setMobileOpen(false); }, [location.pathname]);
    useEffect(() => {
        if (!mobileOpen) return;
        const trigger = menuButton.current;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        drawer.current?.querySelector<HTMLElement>('button, a')?.focus();
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setMobileOpen(false);
            if (event.key !== 'Tab') return;
            const elements = Array.from(drawer.current?.querySelectorAll<HTMLElement>('button, a') || [])
                .filter(element => element.getClientRects().length > 0);
            const first = elements[0];
            const last = elements[elements.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
            if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        };
        const media = window.matchMedia('(min-width: 1024px)');
        const onResize = () => { if (media.matches) setMobileOpen(false); };
        media.addEventListener('change', onResize);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKeyDown);
            media.removeEventListener('change', onResize);
            trigger?.focus();
        };
    }, [mobileOpen]);

    const handleLogout = () => {
        const impersonation = isMerchantImpersonationActive();
        logout();
        if (!impersonation) navigate(cabinetPath('merchant', '/login'));
    };

    return (
        <div className={`merchant-shell min-h-screen bg-gray-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100 ${collapsed ? 'merchant-shell-collapsed' : ''}`}>
            {mobileOpen && <button className="fixed inset-0 z-40 bg-slate-950/50 lg:hidden" aria-label="Закрыть меню" onClick={() => setMobileOpen(false)} />}
            <aside ref={drawer} id="merchant-navigation" role={mobileOpen ? 'dialog' : undefined} aria-modal={mobileOpen || undefined}
                aria-label="Меню мерчанта" className={`merchant-sidebar ${mobileOpen ? 'merchant-sidebar-open' : ''}`}>
                <div className="flex h-20 items-center gap-3 border-b border-slate-200 px-5 dark:border-slate-800">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white"><Building2 className="h-5 w-5" /></div>
                    <div className="merchant-nav-label min-w-0 flex-1"><p className="font-semibold">Кабинет мерчанта</p></div>
                    <Button variant="ghost" size="icon" className="lg:hidden shrink-0" aria-label="Закрыть меню" onClick={() => setMobileOpen(false)}><X className="h-5 w-5" /></Button>
                </div>
                <nav aria-label="Разделы кабинета" className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
                    {sections.filter(section => section.path !== '/integration' || !user?.hide_api_keys).map(({ path, label, icon: Icon }) => {
                        const active = current?.path === path || (path === '/integration' && Boolean(toolPath));
                        return <Link key={path} to={cabinetPath('merchant', path)} title={label} aria-label={label}
                            aria-current={active ? 'page' : undefined} onClick={() => setMobileOpen(false)}
                            className={`flex min-h-[48px] items-center gap-3 rounded-xl px-4 text-sm font-medium transition-colors ${active ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
                            <Icon className="h-5 w-5 shrink-0" /><span className="merchant-nav-label">{label}</span>
                        </Link>;
                    })}
                </nav>
                <div className="border-t border-slate-200 p-3 dark:border-slate-800">
                    <Button variant="ghost" className="hidden w-full justify-center lg:flex" aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'} aria-expanded={!collapsed}
                        onClick={() => setCollapsed(value => !value)}>
                        {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <><PanelLeftClose className="mr-3 h-5 w-5" /><span>Свернуть меню</span></>}
                    </Button>
                </div>
            </aside>
            <div className="merchant-workspace min-w-0">
                <header className="border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                    <div className="mx-auto flex min-h-[80px] max-w-[1600px] flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6 lg:px-8">
                        <div className="flex min-w-0 items-center gap-3">
                            <Button ref={menuButton} variant="outline" size="icon" className="shrink-0 lg:hidden" aria-label="Открыть меню" aria-expanded={mobileOpen} aria-controls="merchant-navigation" onClick={() => setMobileOpen(true)}><Menu className="h-5 w-5" /></Button>
                            <div className="min-w-0"><h1 className="text-xl font-semibold tracking-tight">{title}</h1><p className="break-all text-sm text-slate-500 dark:text-slate-400">{user?.company_name || user?.username} · {resolveMerchantFiatCurrency(user)} / USDT</p></div>
                        </div>
                        <div className="flex items-center gap-2"><ThemeToggle /><Button variant="outline" onClick={handleLogout}><LogOut className="mr-2 h-4 w-4" />{user?.impersonation ? 'В админку' : 'Выйти'}</Button></div>
                    </div>
                </header>
                <main className="merchant-content mx-auto w-full max-w-[1600px] space-y-6 p-4 sm:p-6 lg:p-8">
                    {user?.impersonation && <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">Административный вход: {user.username}. Сессия мерчанта продолжает работать независимо.</div>}
                    {user?.hide_api_keys && (current?.path === '/integration' || toolPath)
                        ? <Navigate to={cabinetPath('merchant', '/dashboard')} replace />
                        : <Outlet />}
                </main>
            </div>
        </div>
    );
}
